/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

(function () {
  'use strict';

  const CHECKOUT_SELECTOR = '.cart__checkout-button, .mini-cart__drawer-footer .checkout-button';

  const locationIcon = `
    <svg aria-hidden="true" viewBox="0 0 24 24" fill="none">
      <path d="M12 21s6-5.1 6-11a6 6 0 1 0-12 0c0 5.9 6 11 6 11Z" stroke="currentColor" stroke-width="1.8"/>
      <circle cx="12" cy="10" r="2.2" stroke="currentColor" stroke-width="1.8"/>
    </svg>`;

  function getErrorMessage(error) {
    if (error && error.code === 1) {
      return 'Autorisez la localisation dans votre navigateur, puis réessayez.';
    }

    if (error && error.code === 2) {
      return 'Votre position est indisponible. Activez le GPS, puis réessayez.';
    }

    if (error && error.code === 3) {
      return 'La localisation a pris trop de temps. Veuillez réessayer.';
    }

    return 'Impossible d’enregistrer votre position. Veuillez réessayer.';
  }

  async function saveLocation(position, card) {
    const latitude = position.coords.latitude.toFixed(6);
    const longitude = position.coords.longitude.toFixed(6);
    const accuracy = Math.round(position.coords.accuracy);
    const mapsUrl = `https://maps.google.com/?q=${latitude},${longitude}`;
    const root = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';

    const response = await fetch(`${root}cart/update.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({
        attributes: {
          'Position de livraison': mapsUrl,
          'Coordonnées GPS': `${latitude}, ${longitude}`,
          'Précision GPS': `${accuracy} m`
        }
      })
    });

    if (!response.ok) {
      throw new Error('Cart location update failed');
    }

    const status = card.querySelector('.careza-location__status');
    const button = card.querySelector('.careza-location__button');
    const link = card.querySelector('.careza-location__link');

    card.classList.add('is-saved');
    button.disabled = false;
    button.removeAttribute('aria-busy');
    button.querySelector('.careza-location__button-label').textContent = 'Position enregistrée';
    status.textContent = 'Votre position sera transmise avec la commande.';
    link.href = mapsUrl;
    link.hidden = false;
  }

  function requestLocation(card) {
    const button = card.querySelector('.careza-location__button');
    const status = card.querySelector('.careza-location__status');
    const link = card.querySelector('.careza-location__link');

    link.hidden = true;
    card.classList.remove('is-saved', 'has-error');
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    button.querySelector('.careza-location__button-label').textContent = 'Localisation en cours…';
    status.textContent = 'Veuillez autoriser l’accès à votre position.';

    if (!navigator.geolocation) {
      button.disabled = false;
      button.removeAttribute('aria-busy');
      button.querySelector('.careza-location__button-label').textContent = 'Utiliser ma position';
      card.classList.add('has-error');
      status.textContent = 'La localisation n’est pas disponible sur ce navigateur.';
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        saveLocation(position, card).catch(() => {
          button.disabled = false;
          button.removeAttribute('aria-busy');
          button.querySelector('.careza-location__button-label').textContent = 'Réessayer';
          card.classList.add('has-error');
          status.textContent = 'La position a été trouvée, mais son enregistrement a échoué.';
        });
      },
      (error) => {
        button.disabled = false;
        button.removeAttribute('aria-busy');
        button.querySelector('.careza-location__button-label').textContent = 'Réessayer';
        card.classList.add('has-error');
        status.textContent = getErrorMessage(error);
      },
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 0
      }
    );
  }

  function createLocationCard() {
    const card = document.createElement('div');
    card.className = 'careza-location';
    card.innerHTML = `
      <div class="careza-location__heading">
        <span class="careza-location__pin">${locationIcon}</span>
        <span>
          <strong>Livraison plus précise</strong>
          <small>Partagez votre position actuelle avec notre livreur.</small>
        </span>
      </div>
      <button class="careza-location__button" type="button">
        <span class="careza-location__button-icon">${locationIcon}</span>
        <span class="careza-location__button-label">Utiliser ma position</span>
      </button>
      <p class="careza-location__status" aria-live="polite">Facultatif · Votre adresse reste nécessaire.</p>
      <a class="careza-location__link" href="#" target="_blank" rel="noopener" hidden>Voir la position sur Google Maps</a>`;

    card.querySelector('.careza-location__button').addEventListener('click', () => requestLocation(card));
    return card;
  }

  function mountLocationButtons() {
    document.querySelectorAll(CHECKOUT_SELECTOR).forEach((checkoutButton) => {
      if (checkoutButton.dataset.carezaLocationMounted === 'true') return;

      checkoutButton.dataset.carezaLocationMounted = 'true';
      checkoutButton.parentElement.insertBefore(createLocationCard(), checkoutButton);
    });
  }

  function initialize() {
    mountLocationButtons();

    const observer = new MutationObserver(mountLocationButtons);
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }
})();

/* careza enhancements */
(() => {
    const enhanceCollections = () => {
        const section = document.querySelector('#shopify-section-template--19046268928274__collection_list_JRUUJH');
            if (!section) return;
                const labels = {'/collections/shampoo':['Soin capillaire','Shampooing'],'/collections/deodorant':['Fraîcheur','Déodorants'],'/collections/gel-douche':['Rituel quotidien','Gel douche']};
                    section.querySelectorAll('.list-collections__item').forEach((card) => {
                          if (card.querySelector('.careza-collection-meta')) return;
                                const href = new URL(card.href, window.location.origin).pathname;
                                      const label = labels[href];
                                            if (!label) return;
                                                  card.setAttribute('aria-label', label[1]);
                                                        const meta = document.createElement('span');
                                                              meta.className = 'careza-collection-meta';
                                                                    meta.innerHTML = `<span class="careza-collection-kicker">${label[0]}</span><strong class="careza-collection-title"><span>${label[1]}</span><span class="careza-collection-arrow" aria-hidden="true">→</span></strong>`;
                                                                          card.append(meta);
                                                                              });
                                                                                };
                                                                                  const addWhatsApp = () => {
                                                                                      const footer = document.querySelector('.footer');
                                                                                          if (!footer || footer.querySelector('.careza-whatsapp')) return;
                                                                                              const link = document.createElement('a');
                                                                                                  link.className = 'careza-whatsapp';
                                                                                                      link.href = 'https://wa.me/212781369175';
                                                                                                          link.target = '_blank';
                                                                                                              link.rel = 'noopener';
                                                                                                                  link.setAttribute('aria-label', 'Contacter Careza sur WhatsApp');
                                                                                                                      link.innerHTML = '<svg viewBox="0 0 32 32" aria-hidden="true"><path d="M16 3a13 13 0 0 0-11.2 19.6L3 29l6.6-1.7A13 13 0 1 0 16 3Zm0 23.6c-2.1 0-4.1-.6-5.8-1.6l-.4-.2-3.9 1 1-3.8-.3-.4A10.5 10.5 0 1 1 16 26.6Zm5.8-7.9c-.3-.2-1.9-.9-2.2-1-.3-.1-.5-.2-.7.2-.2.3-.8 1-.9 1.2-.2.2-.3.2-.7.1-1.8-.9-3-1.7-4.2-3.8-.3-.6.3-.6.9-1.3.1-.2.2-.3.3-.5.1-.2 0-.4 0-.5-.1-.2-.7-1.7-1-2.4-.3-.7-.6-.6-.9-.6h-.7c-.3 0-.7.1-1 .5-.3.4-1.3 1.3-1.3 3.2s1.4 3.7 1.6 4c.2.3 2.7 4.1 6.5 5.8.9.4 1.6.6 2.2.8.9.3 1.7.3 2.4.2.7-.1 1.9-.8 2.2-1.5.3-.7.3-1.4.2-1.5-.1-.1-.4-.2-.7-.4Z"/></svg><span>WhatsApp Careza</span>';
                                                                                                                          const target = footer.querySelector('.footer__item--links, .footer__item--text') || footer;
                                                                                                                              target.append(link);
                                                                                                                                };
                                                                                                                                  const run = () => { enhanceCollections(); addWhatsApp(); };
                                                                                                                                    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run, {once:true}); else run();
                                                                                                                                      document.addEventListener('shopify:section:load', run);
                                                                                                                                      })();

